import axios from 'axios';
import {
  User,
  Project,
  Bug,
  BugStats,
  LoginRequest,
  RegisterRequest,
  CreateBugRequest,
  UpdateBugStatusRequest,
  CreateProjectRequest,
  UpdateProjectRequest,
  AuthResponse
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// 创建axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 无需登录的公开接口（不附加 Authorization，避免网关/其它服务对无效 token 返回 403）
const publicApi = axios.create({
  baseURL: API_BASE_URL,
});

// 请求拦截器 - 添加token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 认证相关API
export const authAPI = {
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  register: async (data: RegisterRequest): Promise<{ message: string; user: User }> => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
};

// 项目相关API
export const projectAPI = {
  getAll: async (): Promise<Project[]> => {
    const response = await api.get('/projects');
    return response.data;
  },

  getById: async (id: string): Promise<Project> => {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  },

  create: async (data: CreateProjectRequest): Promise<Project> => {
    const response = await api.post('/projects', data);
    return response.data;
  },

  update: async (id: string, data: UpdateProjectRequest): Promise<Project> => {
    const response = await api.put(`/projects/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/projects/${id}`);
    return response.data;
  },
};

// BUG相关API
export const bugAPI = {
  getByProject: async (projectId: string, page: number = 1, pageSize: number = 15): Promise<{ bugs: Bug[]; total: number; totalPages: number; currentPage: number }> => {
    const response = await api.get(`/bugs/project/${projectId}?page=${page}&pageSize=${pageSize}`);
    return response.data;
  },

  getStats: async (projectId: string): Promise<BugStats> => {
    const response = await api.get(`/bugs/project/${projectId}/stats`);
    return response.data;
  },

  getById: async (id: string): Promise<Bug> => {
    const response = await api.get(`/bugs/${id}`);
    return response.data;
  },

  create: async (data: CreateBugRequest): Promise<Bug> => {
    const response = await api.post('/bugs', data);
    return response.data;
  },

  createWithImages: async (formData: FormData): Promise<Bug> => {
    const response = await api.post('/bugs/with-images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  updateStatus: async (id: string, data: UpdateBugStatusRequest): Promise<Bug> => {
    const response = await api.patch(`/bugs/${id}/status`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/bugs/${id}`);
    return response.data;
  },
};

// 用户管理API
export const userAPI = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get('/users');
    return response.data;
  },

  updatePassword: async (userId: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.put(`/users/${userId}/password`, { newPassword });
    return response.data;
  },

  updateRole: async (
    userId: string,
    role: 'admin' | 'user'
  ): Promise<{ message: string; user: User; token?: string }> => {
    const response = await api.put(`/users/${userId}/role`, { role });
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
};

export const settingsAPI = {
  getAllowRegister: async (): Promise<{ allowRegister: boolean }> => {
    const response = await publicApi.get('/settings/allow-register');
    return response.data;
  },
  setAllowRegister: async (allowRegister: boolean): Promise<{ message: string; allowRegister: boolean }> => {
    const response = await api.post('/settings/allow-register', { allowRegister });
    return response.data;
  },
};

export interface ProjectShareLinkSummary {
  id: string;
  token: string;
  pin: string;
  createdAt: string;
  revokedAt: string | null;
  active: boolean;
}

export interface CreateShareResponse {
  id: string;
  token: string;
  pin: string;
  createdAt: string;
}

/** 登录用户管理项目分享链接 */
export const shareLinkAPI = {
  create: async (projectId: string): Promise<CreateShareResponse> => {
    const response = await api.post('/share-links', { projectId });
    return response.data;
  },
  listByProject: async (projectId: string): Promise<ProjectShareLinkSummary[]> => {
    const response = await api.get(`/share-links/project/${projectId}`);
    return response.data;
  },
  revoke: async (shareId: string, projectId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/share-links/${shareId}`, { params: { projectId } });
    return response.data;
  },
};

export interface ShareInfoResponse {
  valid: boolean;
  projectName?: string;
}

export interface ShareUnlockResponse {
  accessToken: string;
  projectId: string;
}

/** 客户公开页：无需登录账号 */
export const sharePublicAPI = {
  getInfo: async (token: string): Promise<ShareInfoResponse> => {
    const response = await publicApi.get(`/share/info/${token}`);
    return response.data;
  },
  unlock: async (token: string, pin: string): Promise<ShareUnlockResponse> => {
    const response = await publicApi.post('/share/unlock', { token, pin });
    return response.data;
  },
};

/** 客户已解锁后的 API（使用分享 JWT，勿与登录 token 混用） */
export function createShareGuestClient(accessToken: string) {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return {
    getProject: (): Promise<Project> => client.get('/share/project').then((r) => r.data),
    getStats: (): Promise<BugStats> => client.get('/share/stats').then((r) => r.data),
    getBugs: (page: number = 1, pageSize: number = 15) =>
      client.get(`/share/bugs?page=${page}&pageSize=${pageSize}`).then((r) => r.data),
    createWithImages: (formData: FormData): Promise<Bug> =>
      client.post('/share/bugs/with-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((r) => r.data),
  };
}

export function getUploadsBaseUrl(): string {
  return API_BASE_URL.replace(/\/api\/?$/, '');
}

export default api; 